import Link from "next/link";
import { Fragment } from "react";

// ── Inline markdown renderer ─────────────────────────────────────────────────
// Handles: **bold**, `code`, [label](url), bare /tiles/… paths

type Segment =
  | { type: "text"; value: string }
  | { type: "bold"; value: string }
  | { type: "code"; value: string }
  | { type: "link"; label: string; href: string };

function parseInline(raw: string): Segment[] {
  const pattern =
    /(\*\*(.+?)\*\*|`([^`]+)`|\[([^\]]+)\]\(([^)]+)\)|(\/(?:garden|tiles)\/[\w-]+))/g;
  const segments: Segment[] = [];
  let cursor = 0;
  let match: RegExpExecArray | null;

  while ((match = pattern.exec(raw)) !== null) {
    if (match.index > cursor) {
      segments.push({ type: "text", value: raw.slice(cursor, match.index) });
    }
    if (match[2]) {
      segments.push({ type: "bold", value: match[2] });
    } else if (match[3]) {
      segments.push({ type: "code", value: match[3] });
    } else if (match[4] && match[5]) {
      segments.push({ type: "link", label: match[4], href: match[5] });
    } else if (match[6]) {
      const normalizedHref = match[6].startsWith("/tiles/")
        ? match[6].replace(/^\/tiles\//, "/garden/")
        : match[6];
      segments.push({ type: "link", label: normalizedHref, href: normalizedHref });
    }
    cursor = match.index + match[0].length;
  }

  if (cursor < raw.length) {
    segments.push({ type: "text", value: raw.slice(cursor) });
  }
  return segments;
}

function renderInline(raw: string) {
  return parseInline(raw).map((seg, i) => {
    switch (seg.type) {
      case "bold":
        return <strong key={i}>{seg.value}</strong>;
      case "code":
        return (
          <code
            key={i}
            className="rounded bg-zinc-100 px-1 py-0.5 font-mono text-xs dark:bg-zinc-700"
          >
            {seg.value}
          </code>
        );
      case "link": {
        const isInternal = seg.href.startsWith("/");
        return isInternal ? (
          <Link
            key={i}
            href={seg.href}
            className="font-medium underline underline-offset-2 transition-opacity hover:opacity-70"
          >
            {seg.label}
          </Link>
        ) : (
          <a
            key={i}
            href={seg.href}
            target="_blank"
            rel="noopener noreferrer"
            className="font-medium underline underline-offset-2 transition-opacity hover:opacity-70"
          >
            {seg.label}
          </a>
        );
      }
      default:
        return <Fragment key={i}>{seg.value}</Fragment>;
    }
  });
}

// ── Block-level renderer ─────────────────────────────────────────────────────

function MarkdownContent({ text }: { text: string }) {
  const blocks = text.split(/\n{2,}/);

  return (
    <div className="space-y-2">
      {blocks.map((block, bi) => {
        const lines = block.split("\n");
        const isList = lines.every((l) => /^[-*•]\s/.test(l.trimStart()));

        if (isList) {
          return (
            <ul key={bi} className="space-y-1 pl-4">
              {lines.map((line, li) => (
                <li key={li} className="flex gap-2">
                  <span className="mt-[0.35em] h-1.5 w-1.5 shrink-0 rounded-full bg-current opacity-40" />
                  <span>{renderInline(line.replace(/^[-*•]\s+/, ""))}</span>
                </li>
              ))}
            </ul>
          );
        }

        // Multi-line block → preserve line breaks
        return (
          <p key={bi}>
            {lines.map((line, li) => (
              <Fragment key={li}>
                {li > 0 && <br />}
                {renderInline(line)}
              </Fragment>
            ))}
          </p>
        );
      })}
    </div>
  );
}

// ── Bubble ───────────────────────────────────────────────────────────────────

interface ChatMessageProps {
  role: "user" | "assistant";
  text: string;
}

export function ChatMessage({ role, text }: ChatMessageProps) {
  const isUser = role === "user";

  return (
    <div
      className={`flex items-end gap-2 ${isUser ? "flex-row-reverse" : "flex-row"}`}
    >
      {/* Avatar dot */}
      <div
        className={`mb-1 h-6 w-6 shrink-0 rounded-full ${
          isUser
            ? "bg-zinc-900 dark:bg-white"
            : "border border-zinc-200 bg-white dark:border-zinc-700 dark:bg-zinc-800"
        }`}
      />

      {/* Bubble */}
      <div
        className={`max-w-[78%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
          isUser
            ? "rounded-br-sm bg-zinc-900 text-white dark:bg-white dark:text-zinc-900"
            : "rounded-bl-sm border border-zinc-200 bg-white text-zinc-800 dark:border-zinc-700/60 dark:bg-zinc-900 dark:text-zinc-100"
        }`}
      >
        {isUser ? (
          <span className="whitespace-pre-wrap">{text}</span>
        ) : (
          <MarkdownContent text={text} />
        )}
      </div>
    </div>
  );
}

// ── Typing indicator ─────────────────────────────────────────────────────────

export function TypingIndicator() {
  return (
    <div className="flex items-end gap-2">
      <div className="mb-1 h-6 w-6 shrink-0 rounded-full border border-zinc-200 bg-white dark:border-zinc-700 dark:bg-zinc-800" />
      <div className="rounded-2xl rounded-bl-sm border border-zinc-200 bg-white px-4 py-3 dark:border-zinc-700/60 dark:bg-zinc-900">
        <span className="flex gap-1">
          {[0, 1, 2].map((i) => (
            <span
              key={i}
              className="h-1.5 w-1.5 rounded-full bg-zinc-400 dark:bg-zinc-500"
              style={{
                animation: `bounce 1.2s ease-in-out ${i * 0.2}s infinite`,
              }}
            />
          ))}
        </span>
      </div>
    </div>
  );
}
